import { createSlice, createSelector } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { selectSurveys, setAsset } from "./assetSlice";
import { getLastCreateditem } from "~/utils/utils";
import { addFilesToTree, createTree } from "~/utils/folderTreeUtils";
import _ from "lodash";
import type { CloudImage } from "~/types/CloudImage";

import { fetchSurvey, fetchSurveyFiles, getSurveyData } from "~/thunks/surveyThunks";
import { getAiDetectionsThunk } from "~/thunks/annotationsThunks";
import { fetchAsset } from "~/thunks/assetsThunks";
import { deleteFolderThunk } from "~/thunks/deleteFilesThunk";

import { createModelAndElementsThunk, createThunk } from "~/thunks/createThunks";
import type { RootState } from "~/store";
import type { BridgeModel } from "~/types/bridge";
import { genericDeleteThunk } from "~/thunks/deleteThunks";
import { updateBulkThunk, updateSingleThunk } from "~/thunks/updateThunks";

interface DisplayedSurveyState {
  surveyId: number| string | null;
  models: BridgeModel[];
  // messages: Message[];
  surveyFiles: {
    images: CloudImage[];
    general: Record<string, any>;
  } | null;
  folderTree: any[];
  imagesLocations: any[];
  folderStructure: any[];
  loading: boolean;
  error: string | null;
  heightOffset?: number;
}

const initialState: DisplayedSurveyState = {
  surveyId: null,
  models: [],
  // messages: [],
  surveyFiles: null,
  folderTree: [],
  imagesLocations: [],
  folderStructure: [],
  loading: false,
  error: null,
  heightOffset: 0,
};
const displayedSurveySlice = createSlice({
  name: "displayedSurvey",
  initialState,
  reducers: {
    setSurveyId: (state, action: PayloadAction<string | number>) => {
      state.surveyId = action.payload;
    },
    setSurveyFiles: (state, action: PayloadAction<any>) => {
      state.surveyFiles = action.payload;
    },
    setImagesLocations: (state, action: PayloadAction<any[]>) => {
      state.imagesLocations = action.payload;
    },
    setFolderStructure: (state, action: PayloadAction<any[]>) => {
      state.folderStructure = action.payload;
      const folderTree = createTree(action.payload || []);
      state.folderTree = folderTree;
    },
    setFolderTree: (state, action: PayloadAction<any[]>) => {
      console.log("setFolderTree action", action.payload);
      state.folderTree = action.payload;
    },
    removeModels: (state, action: PayloadAction<number[]>) => {
      const modelIdsToRemove = action.payload;
      state.models = state.models.filter((model) => !modelIdsToRemove.includes(model.id!));
    },
    setModelSubtype: (state, action: PayloadAction<{ id: number; subtype: 'pointCloud' | null }>) => {
      const model = state.models.find((item) => item.id === action.payload.id);
      if (model) {
        model.subtype = action.payload.subtype;
      }
    },

    // addMessage: (state, action: PayloadAction<Message>) => {
    //   console.log("addMessage action", action.payload);
    //   state.messages.push(action.payload);
    // },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSurvey.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSurvey.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("fetchSurvey fulfilled", action.payload);
        // console.log("fetchSurvey fulfilled models", action.payload.models); 
        state.loading = false;
        state.surveyFiles = action.payload.surveyFiles;
        state.imagesLocations = action.payload.imagesLocations;
        // console.log("ADDING MODELS", action.payload.models);
        // console.log("STATE MODELS", state.models);
        let newModels = [...state.models, ...action.payload.models.filter((model: any) => !state.models.some((m: any) => m.id === model.id))]

        state.models = newModels;
        const tileSet = action.payload.models.find((model:any) => model.type == 'model');

        if (tileSet && tileSet.calibration_data) {
          const modelSettings = JSON.parse(tileSet.calibration_data);

          state.heightOffset = +modelSettings.calibrationData.height;
        }

        state.folderStructure = action.payload.folderStructure || [];
        const folderTree = createTree(
          action.payload.folderStructure,
        )
        const filledFolderTree = addFilesToTree(folderTree, action.payload.surveyFiles.images);
        state.folderTree = filledFolderTree;
        // state.survey = action.payload.survey;
      })
            .addCase(updateBulkThunk.fulfilled, (state, action: PayloadAction<{
              dataType: string,
              data: any
            }>) => {
              if (action.payload.dataType == 'bridge_models') {
                console.log("updateBulkThunk fulfilled bridge_models", action.payload, state.models);
                const updatedModels = action.payload.data;
                state.models = state.models.map(model => {
                  const updatedModel = updatedModels.find((um: any) => um.id === model.id);
                  return updatedModel ? { ...model, ...updatedModel } : model;
                });
              };
             
      
            })
      .addCase(fetchSurveyFiles.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("fetchSurveyFiles fulfilled", action.payload);
        state.surveyFiles = action.payload;
        const folderTree = createTree(
          state.folderStructure,
        )
        const filledFolderTree = addFilesToTree(folderTree, action.payload.images);
        state.folderTree = filledFolderTree;
      })
      .addCase(fetchSurvey.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getSurveyData.fulfilled, (state, action: PayloadAction<any>) => {
        const { dataType, data } = action.payload || {};
        console.log("getSurveyData fulfilled", { dataType, data });
        if (dataType === "models" && Array.isArray(data)) {
          state.models = [
            ...state.models,
            ...data.filter((model: any) => !state.models.some((m: any) => m.id === model.id)),
          ];
        }
      })
      .addCase(deleteFolderThunk.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("deleteFilesThunk fulfilled", action.payload);
        
        if (!state.surveyFiles) return;


        const folderTree = createTree(
          state.folderStructure,
        )
        console.log("folderTree", folderTree);
        let updatedSurveyFiles = _.cloneDeep(state.surveyFiles);
        console.log("Current surveyFiles before deletion", updatedSurveyFiles.general);
        if (updatedSurveyFiles.general && Object.keys(updatedSurveyFiles.general).length > 0) {
          Object.keys(updatedSurveyFiles.general).forEach(key => {
            if (key.includes(action.payload)) {
              delete updatedSurveyFiles.general[key];
            }
          })
        }
        if (updatedSurveyFiles.images) {
          updatedSurveyFiles.images = updatedSurveyFiles.images.filter((image: CloudImage) => !image.fullImageName.includes(action.payload));
          console.log("Updated images after deletion", updatedSurveyFiles.images);
          const filledFolderTree = addFilesToTree(folderTree, updatedSurveyFiles.images);
          console.log("Updated folder tree after deletion", filledFolderTree);
          state.folderTree = filledFolderTree;
        }
        state.surveyFiles = updatedSurveyFiles;

      })
      .addCase(fetchAsset.pending, (state, action: PayloadAction<any>) => {
        console.log("displayedSurveySlice fetchAsset.pending", action.payload);
        Object.assign(state, initialState);
      })
      .addCase(fetchAsset.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("displayedSurveySlice fetchAsset.fulfilled", action.payload);
        state.loading = false;
        if (!action.payload || !action.payload.surveys || action.payload.surveys.length === 0) {
          state.surveyId = null;
          return;

        }
        const latestSurvey = getLastCreateditem(action.payload.surveys);
        // console.log("latestSurvey", latestSurvey);
        state.surveyId = latestSurvey.id

        if (action.payload.model) {
          state.models.push(action.payload.model);
        }

      }).
      addCase(getAiDetectionsThunk.fulfilled, (state, action: PayloadAction<any[]>) => {
        
        if (state.surveyFiles && state.surveyFiles.images) {
          const imagesWithDetections = state.surveyFiles.images.map((image: CloudImage) => {
            const detections = action.payload.filter((detection: any) => detection.imageLink === image.fullImageLink);
            return {
              ...image,
              aiDetections: detections,
            };
          });
          state.surveyFiles.images = imagesWithDetections;
        }
      })
      .addCase(setAsset, (state, action: PayloadAction<any>) => {
        console.log("setAsset action", action.payload);
        state.surveyId = null;
        state.models = [];
        state.surveyFiles = null;
        state.imagesLocations = [];
        state.folderTree = [];
        state.folderStructure = [];
        state.loading = false;
        state.error = null;
      })
      .addCase(createThunk.fulfilled, (state, action: PayloadAction<any>) => {
        if (action.payload && action.payload.dataType === 'bridge_models') {
          console.log("createThunk fulfilled", action.payload);
          state.models = [...state.models, action.payload.data];
        }
      })
      .addCase(createModelAndElementsThunk.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("createModelAndElementsThunk fulfilled", action.payload);
        if (action.payload && action.payload.createdModel && action.payload.createdModel.data) {
          state.models = [...state.models, action.payload.createdModel.data];
          
        }
        
      })
      .addCase(genericDeleteThunk.fulfilled, (state, action: PayloadAction<any>) => {
        const operations = action.payload;
        operations.forEach((operation: any) => {
        if (operation.table === 'tbl_bridge_models') {
            const deletedModelIds = operation.ids;
            state.models = state.models.filter(model => !deletedModelIds.includes(model.id));

          }
        })
      })
      .addCase(updateSingleThunk.fulfilled, (state, action: PayloadAction<{
          dataType: string,
          data: any
        }>) => {
  
          if (action.payload.dataType == 'bridge_models') {

            const updatedModel = action.payload.data;
            const modelIndex = state.models.findIndex(model => model.id === updatedModel.id);
            if (modelIndex !== -1) {
              state.models[modelIndex] = { ...state.models[modelIndex], ...updatedModel };
            }
          }
        })
  },
});

export default displayedSurveySlice.reducer;
export const { setSurveyId, setFolderStructure, setSurveyFiles, setImagesLocations, setFolderTree,
  removeModels, setModelSubtype } =
  displayedSurveySlice.actions;
export const selectSurveyId = (state: RootState) => state.displayedSurvey.surveyId;
export const selectSurveyFolderStructure = (state: RootState) => state.displayedSurvey.folderStructure;
export const selectModels = (state: RootState) => state.displayedSurvey.models;
export const selectSurveyFiles = (state: RootState) => state.displayedSurvey.surveyFiles;
export const selectImagesLocations = (state: RootState) => state.displayedSurvey.imagesLocations;
export const selectImagesFolderTree = (state: RootState) => state.displayedSurvey.folderTree;
export const selectHeightOffset = (state: RootState) => state.displayedSurvey.heightOffset;

export const selectSurvey = createSelector(
  selectSurveyId, selectSurveys,
  (surveyId, surveys) => {
    // console.log("selectSurvey", { surveyId, surveys });
    if (!surveyId || !surveys) return null;
    const survey = surveys.find((survey: any) => survey.id == surveyId);
    // console.log("selectSurvey found", { survey });
    return survey || null;

  } 
);


